import { useState, useMemo, useCallback } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, AlertTriangle, CheckCircle2, Ban, DollarSign, FileText } from "lucide-react";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  waived: "bg-blue-50 text-blue-700 border-blue-200",
};

const TYPE_LABELS: Record<string, string> = {
  loan_late: "Loan Late Repayment",
  contribution_late: "Contribution Late Payment",
};

export default function MyPenalties() {
  const { user } = useAuth();
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "appliedDate", desc: true }]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  const { data: penaltiesData, isLoading } = useQuery(
    ["my-penalties", typeFilter, statusFilter],
    async () => {
      const params: any = {};
      if (typeFilter !== "all") params.penaltyType = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/penalties/my", { params });
      return data.data || [];
    }
  );

  const { data: summary } = useQuery(
    ["my-penalty-summary"],
    async () => {
      const { data } = await api.get("/penalties/my/summary");
      return data.data;
    }
  );

  const penalties = penaltiesData || [];

  const filteredData = useMemo(() => {
    let result = [...penalties];
    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter((p: any) =>
        p.reason?.toLowerCase().includes(q) ||
        p.period?.toLowerCase().includes(q) ||
        TYPE_LABELS[p.penaltyType]?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [penalties, searchText]);

  const pageCount = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = useMemo(() =>
    filteredData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [filteredData, pageIndex, pageSize]
  );

  const handleSearch = useCallback((val: string) => {
    setSearchText(val);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "appliedDate",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const d = new Date(row.getValue("appliedDate"));
        return <div className="whitespace-nowrap text-sm">{format(d, "MMM dd, yyyy")}</div>;
      },
    },
    {
      accessorKey: "penaltyType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => {
        const type = row.getValue("penaltyType") as string;
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs">
            {TYPE_LABELS[type] || type}
          </Badge>
        );
      },
    },
    {
      accessorKey: "reason",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Reason" />,
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 max-w-[250px] truncate" title={row.getValue("reason")}>
          {row.getValue("reason")}
        </div>
      ),
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
      cell: ({ row }) => (
        <div className="font-semibold text-sm">
          {Number(row.getValue("amount")).toLocaleString()} FRW
        </div>
      ),
    },
    {
      accessorKey: "period",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Period" />,
      cell: ({ row }) => <div className="text-sm text-gray-600">{row.getValue("period") || "-"}</div>,
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return (
          <Badge variant="outline" className={`${STATUS_STYLES[status] || ""} text-xs capitalize`}>
            {status === "pending" ? "Unpaid" : status}
          </Badge>
        );
      },
    },
  ];

  const facets = [
    {
      name: "status",
      title: "Status",
      type: "checkbox",
      options: [
        { label: "Pending", value: "pending" },
        { label: "Paid", value: "paid" },
        { label: "Waived", value: "waived" },
      ],
    },
    {
      name: "penaltyType",
      title: "Type",
      type: "checkbox",
      options: [
        { label: "Loan Penalties", value: "loan_late" },
        { label: "Contribution Penalties", value: "contribution_late" },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-500">
        <Link to="/member/dashboard" className="hover:text-[#009A44] transition-colors">Dashboard</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-700 font-medium">My Penalties</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">My Penalties</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {summary?.count > 0
            ? `You have ${summary.count} pending ${summary.count === 1 ? "penalty" : "penalties"} totaling ${summary.pendingAmount.toLocaleString()} FRW`
            : "You have no pending penalties"}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-amber-700 font-medium">Pending</p>
                <p className="text-base font-bold text-amber-900">
                  {summary?.pendingAmount?.toLocaleString() || 0} FRW
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-green-700 font-medium">Paid</p>
                <p className="text-base font-bold text-green-900">
                  {summary?.paidAmount?.toLocaleString() || 0} FRW
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center">
                <Ban className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-blue-700 font-medium">Waived</p>
                <p className="text-base font-bold text-blue-900">
                  {summary?.waivedAmount?.toLocaleString() || 0} FRW
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px] h-9 text-sm">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="loan_late">Loan Penalties</SelectItem>
            <SelectItem value="contribution_late">Contribution Penalties</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-9 text-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="waived">Waived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={paginatedData}
        facets={facets}
        isLoading={isLoading}
        defaultColumnVisibility={{}}
        onSearch={handleSearch}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        sorting={sorting}
        setSorting={setSorting}
        setPagination={setPagination}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={pageCount}
        isFetching={false}
      />
    </div>
  );
}
